import { Component, ElementRef, HostBinding, OnDestroy, QueryList, ViewChild, ViewChildren,
	ViewEncapsulation } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { forkJoin, of, Subscription, switchMap } from 'rxjs';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgIf, NgFor } from '@angular/common';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import {
	StepClickedEvent,
	StepStatus,
	StepperComponent
} from '../../../../shared/components/stepper/stepper.component';
import { BocClient } from '../../../../shared/models/boc-client-transac';
import { DATETIME_FORMAT_PATTERN,
	DATE_FORMAT_PATTERN_FOR_CREATION_DATE } from '../../../../shared/models/filter-formats';
import { UNIT_OPTIONS } from '../../../../shared/models/list';
import { User } from '../../../../shared/models/user';
import { LocationService } from '../../../../shared/services/location.service';
import { TranslationService } from '../../../../shared/services/translation.service';
import { UserService } from '../../../../shared/services/user.service';
import { OrderBy, OrderByDirection, HYPHEN } from '../../../../shared/utils/constant';
import { padSoldto } from '../../../../shared/utils/padding';
import { AuctionOffer, Bid } from '../../../models/auction-offer';
import { AuctionSettings } from '../../../models/auction-settings';
import { staticTransportModeValues } from '../../../models/mills';
import { TransportPrice } from '../../../models/transport-price';
import { AuctionOfferService, AUCTION_SERVICE } from '../../../services/auction-offer.service';
import { SoldToChangedConfirmationDialogComponent } from '../../utils/sold-to-changed-confirmation-dialog.component';
import { NumberPipe } from '../../../../shared/pipes/number.pipe';
import { DatePipe } from '../../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { PageHeaderComponent } from '../../../../shared/components/page-header/page-header.component';
import { safe } from '../../../../shared/utils/safe';

@Component({
	selector: 'copo-auction-offer-bidding-screen',
	templateUrl: './auction-offer-bidding-screen.component.html',
	styleUrl: './auction-offer-bidding-screen.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		CdkScrollable,
		PageHeaderComponent,

		MatButtonModule,
		TranslateModule,
		MatIconModule,
		StepperComponent,

		MatFormFieldModule,

		MatSelectModule,
		NgFor,

		MatOptionModule,
		FormsModule,
		ReactiveFormsModule,

		MatInputModule,
		MatExpansionModule,
		MatDividerModule,
		LoadingSpinnerComponent,
		DatePipe,
		NumberPipe
	]
})
export class AuctionOfferBiddingScreenComponent implements OnDestroy {
	@ViewChildren('bundleDiv', { read: ElementRef }) bundleDivs!: QueryList<ElementRef<HTMLElement>>;
	@ViewChild('soldToDiv', { read: ElementRef }) soldToDiv!: ElementRef<HTMLElement>;
	@HostBinding('class.passthrough') readonly passthrough = true;

	auctionOffer!: AuctionOffer;
	offerSub?: Subscription;
	isLoading = true;
	isShipToLoading = false;
	user: User;
	defaultSoldTo: string;
	soldTos: BocClient[] | undefined;
	auctionSettings!: AuctionSettings[];
	remainingTime!: moment.Duration;
	selectedBundles = new Set<number>();
	shiptoList!: BocClient[];
	transportCosts = new Map<number, TransportPrice>();
	incoterms1 = new Map<number, string>();
	incoterms2 = new Map<number, string>();
	effectiveprices = new Map<number, number>();
	transportCostsSub?: Subscription;
	materialPanelExpandedmap = new Map<number, boolean>();
	shipToSubscription?: Subscription;
	soldToChangeSubscription?: Subscription;
	routerParamChangeSub?: Subscription;

	readonly UNDERSCORE = HYPHEN;
	readonly unitBatchOptions = UNIT_OPTIONS;

	readonly steps;

	bundleFormArray = new FormArray<FormGroup<{
		deliveryAddress: FormControl<string | null>;
		transportMode: FormControl<string | null>;
		typeOfMaritimeTransport: FormControl<string | null>;
		customerReference: FormControl<string | null>;
		exwPrice: FormControl<number | null>;
	}>>([]);

	bundleFormArraySubscriptions: Subscription[] = [];

	constructor(
		private readonly auctionOfferService: AuctionOfferService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly translationService: TranslationService,
		private readonly locationService: LocationService,
		private readonly userService: UserService,

		private readonly snackBar: MatSnackBar,

		private readonly dialog: MatDialog,
	) {
		this.steps = [
			{
				label: this.translationService.getTR('auction.selection'),
				status: StepStatus.ENABLED,
			},
			{
				label: this.translationService.getTR('auction.bids'),
				status: StepStatus.CURRENT,
			},
			{
				label: this.translationService.getTR('auction.confirmation'),
				status: StepStatus.DISABLED,
			}
		];
		const resolver = <{ auctionOffer: AuctionOffer }> this.route.snapshot.data;
		this.auctionOffer = resolver['auctionOffer'];
		this.defaultSoldTo = <string> this.route.snapshot.params['soldTo'];

		this.user = this.userService.user;
		this.routerParamChangeSub = this.route.params.subscribe(() => {
			this.defaultSoldTo = <string> this.route.snapshot.params['soldTo'];
			this.reInitialize();
		});
	}

	reInitialize() {
		this.isShipToLoading = true;
		this.transportCosts.clear();
		this.incoterms1.clear();
		this.incoterms2.clear();
		this.effectiveprices.clear();
		this.selectedBundles.clear();
		this.offerSub = forkJoin([
			this.userService.getSoldTosTransac(AUCTION_SERVICE),
			this.auctionOfferService.getAuctionSettings(
				'soldtos',
				0,
				100,
				[new OrderBy('soldtos', OrderByDirection.DESC)],
				false,
				false
			)

		]).pipe(switchMap(([soldtos, auctionSettings]) => {
			this.auctionSettings = auctionSettings.results;
			this.remainingTime = this.calculateremainingTime();

			return forkJoin([
				this.getDeliveryAddressOptions(),
				this.userService.getBocs(
					['name', 'businessId'],
					soldtos.map(x => `businessId:${ x }`).join(' OR ')
				)
			]);
		})).subscribe({
			next: ([shiptos, soldtos]) => {
				this.shiptoList = shiptos ? shiptos.results : [];
				this.soldTos = soldtos.results;
				this.bundleFormArray = new FormArray(this.auctionOffer.bundles.map(
					bundle => this.createBundleFormGroup(
						bundle.bids.find(v => v.soldTo.sapCode === Number(this.defaultSoldTo))
					)
				));
				for (const [index, bundle] of this.auctionOffer.bundles.entries()) {
					// if the customer has already placed a bid for this bundle
					// initialize incoterm, transport cost, effective price
					if (bundle.bids.some(bid => bid.soldTo.sapCode === Number(this.defaultSoldTo))) {
						this.selectedBundles.add(index);
						this.initializeData(index);
					}
					this.materialPanelExpandedmap.set(bundle.id, false);
				}
			},
			complete: () => {
				this.isLoading = false;
				this.isShipToLoading = false;
			}
		});
	}

	ngOnDestroy() {
		this.offerSub?.unsubscribe();
		this.transportCostsSub?.unsubscribe();
		this.shipToSubscription?.unsubscribe();
		this.soldToChangeSubscription?.unsubscribe();
		this.routerParamChangeSub?.unsubscribe();
		for (const subscription of this.bundleFormArraySubscriptions) {
			subscription.unsubscribe();
		}
	}

	createBundleFormGroup(bid?: Bid) {
		const deliveryAddress = new FormControl(
			bid ? bid.shipTo.sapCodeStr : null,
			{
				validators: [ctrl => Validators.required(ctrl)]
			}
		);
		if (bid) {
			const sub = deliveryAddress.valueChanges.subscribe(val => {
				bid.shipTo.sapCodeStr = val !== null ? val : '';
			});
			this.bundleFormArraySubscriptions.push(sub);
		}
		const transportMode = new FormControl(
			{ value: bid ? bid.transportModeDescription : null, disabled: deliveryAddress.value === null },
			{
				validators: [ctrl => Validators.required(ctrl)]
			}
		);
		if (bid) {
			const sub = transportMode.valueChanges.subscribe(val => {
				bid.transportModeDescription = val !== null ? val : '';
			});
			this.bundleFormArraySubscriptions.push(sub);
		}
		const typeOfMaritimeTransport = new FormControl(
			{ value: bid ? bid.typeOfMaritimeTransportDescription : null, disabled: true }
		);
		if (bid) {
			const sub = typeOfMaritimeTransport.valueChanges.subscribe(val => {
				bid.typeOfMaritimeTransportDescription = val !== null ? val : '';
			});
			this.bundleFormArraySubscriptions.push(sub);
		}
		const customerReference = new FormControl(
			bid ? bid.customerReference : null,
			{
				validators: [ctrl => Validators.required(ctrl)]
			}
		);
		if (bid) {
			const sub = customerReference.valueChanges.subscribe(val => {
				bid.customerReference = val !== null ? val : '';
			});
			this.bundleFormArraySubscriptions.push(sub);
		}
		const exwPrice = new FormControl(
			bid ? bid.price : null,
			{
				validators: [ctrl => Validators.required(ctrl)]
			}
		);
		if (bid) {
			const sub = exwPrice.valueChanges.subscribe(val => {
				bid.price = val !== null ? val : 0;
			});
			this.bundleFormArraySubscriptions.push(sub);
		}
		return new FormGroup({
			deliveryAddress,
			transportMode,
			typeOfMaritimeTransport,
			customerReference,
			exwPrice
		});
	}

	getDeliveryAddressOptions() {
		const [auctionSetting] = this.auctionSettings;
		const { soldtos } = auctionSetting;
		const shiptos = soldtos.filter(t => t.sapCode === this.defaultSoldTo).flatMap(v => v.shiptos);
		const businessIds = shiptos.flatMap(v => v.sapCode);
		if (businessIds.length === 0) {
			return of(undefined);
		}
		return this.userService.getBocs(
			['name', 'businessId'],
			businessIds.map(x => `businessId:${ x }`).join(' OR ')
		);
	}

	getTransportModeOptions(shipTo: string, millCode: number) {
		const [auctionSettings] = this.auctionSettings;
		const { soldtos } = auctionSettings;
		const shiptos = soldtos.filter(t => t.sapCode === this.defaultSoldTo).flatMap(v => v.shiptos);
		const mill = shiptos.filter(t => t.sapCode === shipTo).flatMap(v => v.mills)
			.find(m => m.millCode === millCode.toString());
		if (!mill) {
			return [];
		}
		return mill.settings.map(s => staticTransportModeValues.get(s.transportMode))
			.filter((v): v is NonNullable<typeof v> => v !== undefined);
	}

	getIncoterm(shipTo: string, tmode: string, millCode: number) {
		const [auctionSettings] = this.auctionSettings;
		const { soldtos } = auctionSettings;
		const shiptos = soldtos.filter(t => t.sapCode === this.defaultSoldTo).flatMap(v => v.shiptos);
		const mill = shiptos.filter(t => t.sapCode === shipTo).flatMap(v => v.mills)
			.find(m => m.millCode === millCode.toString());
		if (!mill) {
			return undefined;
		}
		const desiredMillSettings = mill.settings.find(v => v.transportMode === tmode);
		if (desiredMillSettings) {
			return [desiredMillSettings.incoterm1, desiredMillSettings.incoterm2];
		}
		return undefined;
	}

	checkIsDeliveryAddressFilled(i: number) {
		return this.bundleFormArray.controls[i].controls.deliveryAddress.value === null;
	}

	checkIsMaritimeTransportModeSelected(i: number) {
		return this.bundleFormArray.controls[i].controls.transportMode.value === 'MARIT';
	}

	soldToChanged(event: MatSelectChange) {
		const soldto = <string> event.value;
		const sub = this.dialog.open<SoldToChangedConfirmationDialogComponent>(
			SoldToChangedConfirmationDialogComponent,
			{ panelClass: 'copo-claim-dialog', disableClose: true }
		);

		this.soldToChangeSubscription = sub.afterClosed().subscribe(async result => {
			const confirm = Boolean(result);
			if (confirm) {
				await this.router.navigate(['../', soldto], { relativeTo: this.route });
			}
		});
	}

	onDeliveryAddressChange(i: number) {
		this.selectedBundles.add(i);
		const shipTo = this.bundleFormArray.controls[i].controls.deliveryAddress.value;
		if (shipTo) {
			this.bundleFormArray.controls[i].controls.transportMode.enable();
			const tmodeList = this.getTransportModeOptions(shipTo, this.auctionOffer.bundles[i].mill.pp39);
			if (tmodeList.length === 1) {
				this.bundleFormArray.controls[i].controls.transportMode.setValue(tmodeList[0].description);
				this.onTransportModeChange(i);
			} else {
				this.bundleFormArray.controls[i].controls.transportMode.setValue(null);
				this.transportCosts.delete(i);
				this.incoterms1.delete(i);
				this.incoterms2.delete(i);
				this.effectiveprices.delete(i);
			}
		} else {
			this.bundleFormArray.controls[i].controls.transportMode.disable();
		}
	}

	onTransportModeChange(i: number) {
		if (this.bundleFormArray.controls[i].controls.transportMode.value !== null) {
			if (this.checkIsMaritimeTransportModeSelected(i)) {
				this.bundleFormArray.controls[i].controls.typeOfMaritimeTransport.enable();
			} else {
				this.bundleFormArray.controls[i].controls.typeOfMaritimeTransport.disable();
			}
			this.initializeData(i);
		}
	}

	initializeData(i: number) {
		const shipto = safe(this.bundleFormArray.controls[i].controls.deliveryAddress.value);
		const transportMode = safe(this.bundleFormArray.controls[i].controls.transportMode.value);
		const transportModecode = staticTransportModeValues.get(transportMode)?.code.toString();
		const incoterm = this.getIncoterm(shipto, transportMode, this.auctionOffer.bundles[i].mill.pp39);
		const millsyst = this.auctionOffer.bundles[i].mill.pp39.toString().padStart(7, '0');
		const date = moment(Date.now()).format(DATE_FORMAT_PATTERN_FOR_CREATION_DATE);
		if (incoterm) {
			this.incoterms1.set(i, incoterm[0]);
			this.incoterms2.set(i, incoterm[1]);
		}
		if (shipto && transportModecode && incoterm && incoterm[0] !== '') {
			this.transportCostsSub = this.auctionOfferService
				.getTransportCost(date, incoterm[0], millsyst, transportModecode, padSoldto(shipto))
				.subscribe(res => {
					this.transportCosts.set(i, res);
					this.effectiveprices.set(i, this.calculateEffectivePrice(i));
				});
		}
	}

	getTransportCostAsNumber(i: number) {
		if (this.transportCosts.get(i)?.transportPrice
			&& !Number.isNaN(Number(this.transportCosts.get(i)?.transportPrice))) {
			return Number(this.transportCosts.get(i)?.transportPrice);
		}
		return undefined;
	}

	onExwPriceChange(event: Event, i: number) {
		const target = <HTMLInputElement> event.target;
		this.bundleFormArray.controls[i].controls.exwPrice.setValue(Number(Number.parseFloat(target.value).toFixed(2)));
		this.effectiveprices.set(i, this.calculateEffectivePrice(i));
	}

	calculateEffectivePrice(i: number) {
		const exwprice = this.bundleFormArray.controls[i].controls.exwPrice.value;
		const transportCost = this.transportCosts.get(i)?.transportPrice;
		return ((exwprice ? exwprice * 100 : 0) + (Number(transportCost) * 100)) / 100;
	}

	calculateremainingTime() {
		const currentDate = moment(moment().format(DATETIME_FORMAT_PATTERN));
		const endDate = moment(this.auctionOffer.endDate);
		return moment.duration(endDate.diff(currentDate));
	}

	checkIfEndDateHasCompleted() {
		const now = new Date();
		return new Date(this.auctionOffer.endDate) < now;
	}

	expandMaterialPanel(bundleId: number) {
		this.materialPanelExpandedmap.set(bundleId, true);
	}

	shrinkMaterialPanel(bundleId: number) {
		this.materialPanelExpandedmap.set(bundleId, false);
	}

	resetForm(i: number) {
		this.bundleFormArray.controls[i].reset();
		this.bundleFormArray.controls[i].controls.transportMode.disable();
		this.transportCosts.delete(i);
		this.incoterms1.delete(i);
		this.incoterms2.delete(i);
		this.effectiveprices.delete(i);
		this.selectedBundles.delete(i);
	}

	async onStepClicked(event: StepClickedEvent) {
		if (event.stepIndex === 0) {
			await this.router.navigate(['../../../offer-selection'], { relativeTo: this.route });
		}
	}

	goBack() {
		this.locationService.go(-1, '../auction/bidding/offer-selection');
	}

	async goToSummary(event: Event) {
		event.preventDefault();
		const soldTo = this.defaultSoldTo;
		if (!soldTo) {
			this.soldToDiv.nativeElement.scrollIntoView();
			return;
		}
		// checking if atleast one bid is placed
		if (Array.from(this.selectedBundles).length === 0) {
			this.snackBar.open(
				this.translationService.getTR(
					'error.place.atleast.one.bid'
				),
				undefined,
				{
					duration: 5000,
				}
			);
			return;
		}
		for (const i of this.selectedBundles) {
			const shipto = this.bundleFormArray.controls[i].controls.deliveryAddress.value;
			const transportMode = this.bundleFormArray.controls[i].controls.transportMode.value;
			const customerReference = this.bundleFormArray.controls[i].controls.customerReference.value;
			const exwPrice = this.bundleFormArray.controls[i].controls.exwPrice.value;
			const incoterm1 = this.incoterms1.get(i);
			const incoterm2 = this.incoterms2.get(i);
			const transportCost = this.transportCosts.get(i);
			const bundleDiv = this.bundleDivs.get(i);
			if (!shipto || !transportMode || !customerReference || !exwPrice) {
				bundleDiv?.nativeElement.scrollIntoView();
				this.bundleFormArray.controls[i].markAllAsTouched();
				this.bundleFormArray.controls[i].updateValueAndValidity();
				return;
			} else if (transportCost?.returncode !== '0') {
				this.snackBar.open(
					this.translationService.getTR(
						'error.inavlid.transport.cost'
					),
					undefined,
					{
						duration: 5000,
					}
				);
				return;
			} else if (!incoterm1) {
				this.snackBar.open(
					this.translationService.getTR(
						'error.inavlid.incoterm'
					),
					undefined,
					{
						duration: 5000,
					}
				);
				return;
			}
			const bundle = this.auctionOffer.bundles[i];
			const currentBid = bundle.bids.find(bid => bid.soldTo.sapCode === Number(soldTo));
			if (currentBid) {
				currentBid.creationUser = `${ this.user.firstname } ${ this.user.lastname }`;
				currentBid.creationDate = moment().toISOString();
				currentBid.creationuserId = Number(soldTo);
				currentBid.customerReference = customerReference;
				const transportModecode = staticTransportModeValues.get(transportMode)?.code;
				currentBid.transportMode = safe(transportModecode);
				currentBid.incoterm = incoterm1;
				if (incoterm2 && incoterm2 !== '') {
					currentBid.incoterm2 = incoterm2;
				}
				currentBid.transportPrice = Number(transportCost.transportPrice);
				currentBid.transportModeDescription = transportMode;
				currentBid.price = exwPrice;
				const customer = {
					name: `${ this.user.firstname } ${ this.user.lastname }`,
					sapCode: Number(soldTo),
					sapCodeStr: soldTo,
				};
				const shipToCustomer = {
					name: this.shiptoList.find(v => v.businessId === shipto)?.name || '',
					sapCode: Number(shipto),
					sapCodeStr: shipto,
				};
				currentBid.soldTo = customer;
				currentBid.shipTo = shipToCustomer;
			} else {
				const bid = <Bid>{};
				bid.creationUser = `${ this.user.firstname } ${ this.user.lastname }`;
				bid.creationDate = moment().toISOString();
				bid.creationuserId = Number(soldTo);
				bid.customerReference = customerReference;
				const transportModecode = staticTransportModeValues.get(transportMode)?.code;
				bid.transportMode = safe(transportModecode);
				bid.incoterm = incoterm1;
				if (incoterm2 && incoterm2 !== '') {
					bid.incoterm2 = incoterm2;
				}
				bid.transportPrice = Number(transportCost.transportPrice);
				bid.transportModeDescription = transportMode;
				bid.price = exwPrice;
				const customer = {
					name: `${ this.user.firstname } ${ this.user.lastname }`,
					sapCode: Number(soldTo),
					sapCodeStr: soldTo,
				};
				const shipToCustomer = {
					name: this.shiptoList.find(v => v.businessId === shipto)?.name || '',
					sapCode: Number(shipto),
					sapCodeStr: shipto,
				};
				bid.soldTo = customer;
				bid.shipTo = shipToCustomer;
				this.auctionOffer.bundles[i].bids.push(bid);
			}
		}

		await this.router.navigate(
			['./confirmation'],
			{ relativeTo: this.route }
		);
	}
}
